import { useEffect, useState } from 'react';

const C = {
  red: '#E8192C',
  orange: '#FF6B2B',
  bg: '#0C0905',
};

export const DriftField = ({ count = 18, fixed = true }) => {
  const [diamonds, setDiamonds] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 24 + Math.random() * 70,
      rot: Math.random() * 360,
      vx: (Math.random() - 0.5) * 0.04,
      vy: (Math.random() - 0.5) * 0.03 - 0.005,
      vrot: (Math.random() - 0.5) * 0.15,
      opacity: 0.04 + Math.random() * 0.08,
      color: Math.random() > 0.8 ? C.orange : C.red,
    }))
  );

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let raf;
    let lastTick = performance.now();
    const tick = (now) => {
      const dt = Math.min(50, now - lastTick);
      lastTick = now;
      const step = dt / 16.67;
      setDiamonds(prev =>
        prev.map(d => {
          let nx = d.x + d.vx * step;
          let ny = d.y + d.vy * step;
          if (nx > 110) nx = -10;
          if (nx < -10) nx = 110;
          if (ny > 110) ny = -10;
          if (ny < -10) ny = 110;
          return { ...d, x: nx, y: ny, rot: d.rot + d.vrot * step };
        })
      );
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: fixed ? 'fixed' : 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      {diamonds.map(d => (
        <span
          key={d.id}
          style={{
            position: 'absolute',
            left: `${d.x}%`,
            top: `${d.y}%`,
            width: d.size,
            height: d.size,
            border: `1.5px solid ${d.color}`,
            opacity: d.opacity,
            transform: `translate(-50%, -50%) rotate(${d.rot}deg)`,
            boxSizing: 'border-box',
          }}
        />
      ))}
    </div>
  );
};

export const Grain = () => (
  <div
    aria-hidden="true"
    style={{
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 3,
      opacity: 0.05,
      mixBlendMode: 'overlay',
      backgroundImage:
        'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/><feColorMatrix values=%220 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
      backgroundSize: '160px 160px',
    }}
  />
);

const heroTint = `
  radial-gradient(circle at 20% 15%, rgba(232, 25, 44, 0.12), transparent 50%),
  radial-gradient(circle at 85% 80%, rgba(255, 107, 43, 0.07), transparent 55%),
  ${C.bg}
`;

export default function RepeakedBackdrop({ children, zIndex = 10000 }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex,
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: heroTint,
        }}
      />
      <DriftField count={18} fixed />
      <Grain />
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}
      >
        {children}
      </div>
    </div>
  );
}
